import Location from "@components/Location/index";
import MapPopup from "@components/MapPopup";
import { useFormContext } from "@hudson/ui/components/ReactHookForm";
import Session from "@lib/session";
import useContent from "@lib/use_content";
import useWindowSize from "@utils/use_window_size";
import { motion } from "motion/react";
import dynamic from "next/dynamic";
import { SyntheticEvent, useEffect } from "react";
import SwapButton from "./SwapButton";

interface PickupAndDropoffProps {
  className?: string;
  configLoading?: boolean;
  disabled?: boolean;
  fieldNames: { pickup: string; dropoff: string };
  findLocations?: be.FindLocationsFunction;
  limitLocations?: number;
  locationPredictionRenderer?: JSX.Element;
  locationPredictionSelected?: be.LocationPredictionSelectedFunction;
  noMap?: boolean;
}

// Named export is a test seam: the default export below is the client-only
// dynamic() wrapper, which cannot be rendered synchronously under test.
export function PickupAndDropoff({
  className,
  configLoading,
  disabled,
  fieldNames,
  findLocations,
  limitLocations,
  locationPredictionRenderer,
  locationPredictionSelected,
  noMap = false,
}: PickupAndDropoffProps): JSX.Element | null {
  const { data, error, loading } = useContent([
    "beErrFieldRequired",
    "bePickupLocLabel",
    "beMapPopupLinkLabel",
    "beDropoffLocLabel",
  ]);
  const [size] = useWindowSize();

  // Hook input into form
  const { clearErrors, setValue, watch } = useFormContext();
  const [pickup, dropoff] = watch([fieldNames.pickup, fieldNames.dropoff]);

  // Try to keep the booking-level service area in sync with the current state of
  // the location selection. This ensures that any service-area-dependant error messages
  // or other content can be as accurate as possible.
  useEffect(() => {
    let serviceArea = Session.getConfig().serviceArea;
    if (typeof pickup?.serviceArea === "number") serviceArea = pickup.serviceArea;
    else if (typeof dropoff?.serviceArea === "number") serviceArea = dropoff.serviceArea;
    else if (typeof pickup?.serviceArea === "object" && typeof dropoff?.serviceArea === "object") {
      // Both pickup and dropoff have service area lists. Pick one of the overlapping areas.
      let intersection = pickup.serviceArea.filter((x: number[]) =>
        dropoff.serviceArea.includes(x),
      );
      if (intersection.length > 0) serviceArea = intersection[0];
    }

    const bookingArea = Session.getBooking().serviceArea;
    if (serviceArea && bookingArea != serviceArea) {
      Session.updateBooking({ serviceArea });
      // Reset the store only when the booking is genuinely SWITCHING areas. A reset
      // never re-keys already-mounted content/config hooks (they recompute their
      // area from the session on their next render regardless), so in these two
      // cases it can only hurt and is skipped:
      // - first mount, where the booking has no area yet and the computed one is
      //   just the configured default — a load-time reset kills the page's own
      //   in-flight config/content queries (Apollo "Store reset while query was in
      //   flight", error #34);
      // - mid-submit (fields disabled): the area only differs here if something
      //   wiped the booking between the last sync and the click (a session expiry
      //   swapping in a fresh Booking), and a reset then rejects the click's own
      //   in-flight content lookup.
      // resetStore() refetches the active queries itself; a preceding stop() would
      // abort EVERY in-flight query on the client, so there is none.
      const firstRecordOfDefault =
        bookingArea == null && serviceArea === Session.getConfig().serviceArea;
      if (!disabled && !firstRecordOfDefault) {
        global.window.apolloClient
          .resetStore()
          .catch((e: unknown) => console.error("Service-area store reset failed:", e));
      }
    }
  }, [pickup, dropoff, disabled]);

  if (error) {
    console.error(error);
    return null;
  }

  const swap = function (e: SyntheticEvent) {
    e.preventDefault();
    setValue(fieldNames.pickup, dropoff, { shouldDirty: !!dropoff, shouldValidate: !!dropoff });
    if (!dropoff) clearErrors(fieldNames.pickup); // Clear errors if we're swapping to an empty field
    setValue(fieldNames.dropoff, pickup, { shouldDirty: !!pickup, shouldValidate: !!pickup });
    if (!pickup) clearErrors(fieldNames.dropoff); // Clear errors if we're swapping to an empty field
  };

  const locationObj = function (
    direction: "pickup" | "dropoff",
    { className = "", placeholder = "" }: { className?: string; placeholder?: string },
  ) {
    const obj = direction == "pickup" ? pickup : dropoff;
    const otherObj = direction == "pickup" ? dropoff : pickup;
    const serviceAreaFilter =
      direction == "dropoff"
        ? Session.getConfig().serviceArea || pickup?.serviceArea
        : Session.getConfig().serviceArea;

    return (
      <Location
        className={className}
        configLoading={configLoading}
        disabled={disabled}
        fieldName={fieldNames[direction]}
        findLocations={findLocations}
        limitLocations={limitLocations}
        locationPredictionRenderer={locationPredictionRenderer}
        locationPredictionSelected={locationPredictionSelected}
        placeholder={placeholder}
        searchCenter={otherObj?.latLng?.lng ? otherObj.latLng : undefined}
        serviceAreaFilter={serviceAreaFilter}
      />
    );
  };

  return (
    <motion.div
      layout
      transition={{
        type: "tween",
        ease: "easeOut",
        duration: 0.2,
      }}
      className={`${
        className ? className : ""
      } relative flex flex-col flex-1 normal:flex-1 h-full `}
    >
      <div className="flex flex-1 normal:flex-1 normal:flex-row flex-col normal:gap-x-1.5">
        {locationObj("pickup", {
          className: "normal:mr-1",
          placeholder: data?.content.bePickupLocLabel,
        })}
        <SwapButton disabled={disabled} onSwap={swap} y={size < 1080 ? -12 : pickup && dropoff ? 14 : 6} />
        <div className="flex flex-col self-start flex-1 w-full normal:w-auto normal:flex-1">
          {locationObj("dropoff", {
            className: "normal:mt-0 mt-3",
            placeholder: data?.content.beDropoffLocLabel,
          })}
        </div>
      </div>
      {!noMap && (
        <MapPopup
          className="absolute text-right  normal:relative normal:bottom-0 normal:text-right norm sm:right-5 right-1 -bottom-8"
          label={data?.content.beMapPopupLinkLabel}
          startPoint={pickup?.latLng}
          endPoint={dropoff?.latLng}
          visible={pickup?.latLng?.lng && dropoff?.latLng?.lng}
        />
      )}
    </motion.div>
  );
}

const LoadingPlaceholder = () => (
  <div className="relative flex flex-col flex-1 normal:flex-1 h-full normal:mr-3">
    <div className="flex flex-1 normal:flex-1 normal:flex-row flex-col normal:gap-x-1.5">
      <div className="normal:mr-1 h-[60px] bg-gray-100 rounded-md animate-pulse flex-1" />
      <div className="flex flex-col self-start flex-1 w-full normal:w-auto normal:flex-1">
        <div className="normal:mt-0 mt-3 h-[60px] bg-gray-100 rounded-md animate-pulse" />
      </div>
    </div>
  </div>
);

export default dynamic(() => Promise.resolve(PickupAndDropoff), {
  ssr: false,
  loading: () => <LoadingPlaceholder />,
});
